import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, Signal } from '@angular/core';
import {
  handleErrorLocallyHttpOptions,
  HandleErrorLocallyOptions
} from '@app/shared/interceptors/handle-error-locally.context';
import { DataService } from '@app/shared/services/data-service';
import { ProjectParticipantDTO } from '@app/shared/services/dtos/project-participants/project-participant.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { GroupUser } from '@app/shared/types/models/group/group-user.model';
import { Group } from '@app/shared/types/models/group/group.model';
import { ProjectParticipant } from '@app/shared/types/models/project/project-participant';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { finalize, map, Observable } from 'rxjs';
import { GroupRole } from '../types/enums/group-role.enum';
import { ActiveProjectService } from './active-project.service';
import { ProjectParticipantFactory } from './factories/project-participant.factory';

/**
 * ProjectParticipantsDataService - Project participants and groups in the data store.
 *
 * Loads participants for {@link ActiveProjectService.activeProjectId} when it changes (constructor `effect`),
 * replacing the previous `ActiveProjectSet` notification hook.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Project participants UI, task filters, and {@link TasksActionsService}.
 *
 * Architecture:
 * - {@link ProjectParticipantsDataService}: Store and HTTP for participants/groups
 * - {@link ActiveProjectService}: Drives default load for the active project
 */
@Injectable({ providedIn: 'root' })
export class ProjectParticipantsDataService extends DataService<ProjectParticipantDTO, ProjectParticipant> {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly projectParticipantFactory: ProjectParticipantFactory = inject(ProjectParticipantFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);

  private participants: DataStore<ProjectParticipant> = new DataStore<ProjectParticipant>();
  private projectParticipants: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public constructor() {
    super();
    effect(() => {
      const projectId = this.activeProjectService.activeProjectId();
      if (!projectId) {
        return;
      }
      this.loadProjectParticipants(projectId);
    });
  }
  public getProjectParticipants(projectId: string): Signal<ProjectParticipant[] | undefined> {
    if (!projectId) return signal(undefined);
    if (!this.projectParticipants.hasDataForId(projectId)) this.fetchProjectParticipants(projectId);

    return computed(() => {
      return (
        this.projectParticipants
          .get(projectId)()
          ?.ids?.map(id => this.participants.get(id)()) || []
      );
    });
  }

  public getProjectGroupAdmin(projectId: string, groupId: string): Signal<ProjectParticipant | undefined> {
    return computed(() => {
      return this.getProjectParticipants(projectId)()?.find(
        pp => pp.group()?.id === groupId && pp.role === GroupRole.ADMIN
      );
    });
  }

  public getProjectGroups(projectId: string): Signal<Group[]> {
    if (!projectId) {
      return signal([]);
    }

    return computed(() => {
      const participants = this.getProjectParticipants(projectId)() ?? [];
      const byGroupId = new Map<string, ProjectParticipant[]>();

      for (const participant of participants) {
        const group = participant.group();
        if (!group?.id) {
          continue;
        }
        const bucket = byGroupId.get(group.id);
        if (bucket) {
          bucket.push(participant);
        } else {
          byGroupId.set(group.id, [participant]);
        }
      }

      const result: Group[] = [];

      for (const groupParticipants of byGroupId.values()) {
        const sourceGroup = groupParticipants[0].group();
        if (!sourceGroup?.id) {
          continue;
        }

        const participantByUserId = new Map<string, ProjectParticipant>();
        for (const participant of groupParticipants) {
          const userId = participant.user()?.id;
          if (!userId) {
            continue;
          }
          if (!participantByUserId.has(userId)) {
            participantByUserId.set(userId, participant);
          }
        }

        const groupUsers = Array.from(participantByUserId.values()).map(
          participant =>
            new GroupUser({
              id: participant.id,
              groupId: sourceGroup.id,
              applicationUser: participant.user,
              role: participant.role
            })
        );

        result.push(
          new Group({
            childrenCount: sourceGroup.childrenCount,
            id: sourceGroup.id,
            name: sourceGroup.name,
            companyId: sourceGroup.companyId,
            parentGroupId: sourceGroup.parentGroupId,
            users: signal(groupUsers)
          })
        );
      }

      result.sort((left, right) =>
        (left.name ?? '').localeCompare(right.name ?? '', undefined, { sensitivity: 'base' })
      );

      return result;
    });
  }

  public loadProjectParticipants(projectId: string): void {
    if (!projectId) {
      return;
    }

    if (
      this.projectParticipants.hasDataForId(projectId) &&
      this.projectParticipants.get(projectId)()?.id !== undefined
    ) {
      return;
    }

    this.fetchProjectParticipants(projectId);
  }

  public getById(participantId: string): Signal<ProjectParticipant | undefined> {
    return computed(() => this.participants.get(participantId)());
  }

  public upsertLocalData(dto: ProjectParticipantDTO): Signal<ProjectParticipant> {
    return dto ? this.participants.upsert(this.projectParticipantFactory.produce(dto)) : signal(undefined);
  }

  protected fetchProjectParticipants(projectId: string): void {
    // this.loaderService.startLoading();

    this.httpClient
      .get<ProjectParticipantDTO[]>(environment.APIEndPoint + `projects/${projectId}/participants`)
      .subscribe({
        next: (projectParticipants: ProjectParticipantDTO[]) => {
          this.participants.upsertMany(
            projectParticipants.map(projectParticipant => this.projectParticipantFactory.produce(projectParticipant))
          );
          this.projectParticipants.upsert({
            id: projectId,
            ids: projectParticipants.map(itm => itm.id)
          });
        }
        // error: () => this.loaderService.stopLoading(),
        // complete: () => this.loaderService.stopLoading()
      });
  }

  public removeUsersFromGroup(
    projectId: string,
    group: Group,
    usersToRemoveIds: string[],
    options?: HandleErrorLocallyOptions
  ): Observable<ProjectParticipantDTO[]> {
    this.loaderService.startLoading();
    return this.httpClient
      .post<ProjectParticipantDTO[]>(
        environment.APIEndPoint + `projects/${projectId}/participants/groups/${group.id}/remove-users`,
        {
          applicationUserIds: usersToRemoveIds
        },
        handleErrorLocallyHttpOptions(options?.handleErrorLocally)
      )
      .pipe(
        map((projectParticipants: ProjectParticipantDTO[]) => {
          this.projectParticipants.upsert({
            id: projectId,
            ids: projectParticipants.map(participant => participant.id)
          });

          usersToRemoveIds.forEach(participantId => this.participants.delete(participantId));

          this.toastService.success(
            this.translateService.instant('project-participants.toasts.user-successfully-removed')
          );
          return projectParticipants;
        }),
        finalize(() => this.loaderService.stopLoading())
      );
  }

  public addUsersToGroup(
    projectId: string,
    group: Group,
    usersToAddIds: string[],
    options?: HandleErrorLocallyOptions
  ): Observable<ProjectParticipant[]> {
    this.loaderService.startLoading();
    return this.httpClient
      .post<ProjectParticipantDTO[]>(
        environment.APIEndPoint + `projects/${projectId}/participants/groups/${group.id}/add-users`,
        {
          applicationUserIds: usersToAddIds
        },
        handleErrorLocallyHttpOptions(options?.handleErrorLocally)
      )
      .pipe(
        map((projectParticipants: ProjectParticipantDTO[]) => {
          const participants = projectParticipants.map(projectParticipant =>
            this.projectParticipantFactory.produce(projectParticipant)
          );
          this.participants.upsertMany(participants);

          this.projectParticipants.upsert({
            id: projectId,
            ids: projectParticipants.map(participant => participant.id)
          });

          this.toastService.success(
            this.translateService.instant('project-participants.toasts.user-successfully-added')
          );
          return participants;
        }),
        finalize(() => this.loaderService.stopLoading())
      );
  }

  public addGroups(projectId: string, groupsToAddIds: string[]): void {
    this.loaderService.startLoading();
    this.httpClient
      .post<ProjectParticipantDTO[]>(environment.APIEndPoint + `projects/${projectId}/participants/groups`, {
        groupIds: groupsToAddIds
      })
      .subscribe({
        next: (addedParticipants: ProjectParticipantDTO[]) => {
          this.participants.upsertMany(
            addedParticipants.map(projectParticipant => this.projectParticipantFactory.produce(projectParticipant))
          );
          const existingGroupChildren: string[] = this.projectParticipants.get(projectId)().ids ?? [];
          this.projectParticipants.upsert({
            id: projectId,
            ids: existingGroupChildren.concat(...addedParticipants.map(participant => participant.id))
          });
          this.toastService.success(
            this.translateService.instant('project-participants.toasts.groups-successfully-added')
          );
        },
        error: () => this.loaderService.stopLoading(),
        complete: () => this.loaderService.stopLoading()
      });
  }

  public removeGroupById(projectId: string, groupId: string): void {
    this.loaderService.startLoading();
    this.httpClient.delete(environment.APIEndPoint + `projects/${projectId}/participants/groups/` + groupId).subscribe({
      next: () => {
        if (this.projectParticipants.hasDataForId(projectId)) {
          const allProjectParticipants = this.projectParticipants.get(projectId)();

          let newProjectParticipants = [...allProjectParticipants.ids];
          const participantsToRemove = [];

          allProjectParticipants.ids.forEach(ppId => {
            if (this.participants.hasDataForId(ppId)) {
              const projectParticpant = this.participants.get(ppId)();
              if (projectParticpant.group().id === groupId) {
                participantsToRemove.push(projectParticpant);
                newProjectParticipants = newProjectParticipants.filter(_ => _ !== ppId);
              }
            }
          });

          this.projectParticipants.upsert({
            id: projectId,
            ids: newProjectParticipants
          });

          participantsToRemove.forEach(pp => this.participants.delete(pp));
        }

        this.toastService.success(
          this.translateService.instant('project-participants.toasts.groups-successfully-removed')
        );
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }
}
